/**
 * AdminModal — floating admin dashboard triggered by `open-admin-modal` CustomEvent.
 *
 * Opened by the SiteNav Admin button. Escape or backdrop click to close.
 * The modal is mounted once in OutlineView and stays hidden until the event fires.
 */

import { useEffect, useState } from 'react'
import { useAuth, useUser } from '@clerk/clerk-react'
import { isSeatLoaded, hasModule, loadCurrentUserSeat } from '../services/tenancy.js'
import AdminDashboard from './admin/AdminDashboard.jsx'
import { useDarkMode } from '../outline/theme.js'

export default function AdminModal() {
  const [open, setOpen]           = useState(false)
  const [seatReady, setSeatReady] = useState(isSeatLoaded())
  const dark                      = useDarkMode()
  const { isSignedIn, getToken }  = useAuth()
  const { user }                  = useUser()

  /* Open on event */
  useEffect(() => {
    function handle() { setOpen(true) }
    // "Open Templates" from Admin (templates moved there 2026-09-24): step aside so the tool is visible.
    function onTemplates() { setOpen(false) }
    window.addEventListener('open-admin-modal', handle)
    window.addEventListener('open-templates-workspace', onTemplates)
    return () => { window.removeEventListener('open-admin-modal', handle); window.removeEventListener('open-templates-workspace', onTemplates) }
  }, [])

  /* Escape to close */
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  /* Sync seatReady whenever the modal opens */
  useEffect(() => {
    if (!open || !user?.id) return
    if (isSeatLoaded()) { setSeatReady(true); return }
    // Seat not loaded yet — kick off loading (OutlineView may still be in flight)
    window.__clerkGetToken = () => getToken()
    loadCurrentUserSeat(user.id).then(() => setSeatReady(true))
  }, [open, user?.id, getToken])

  if (!open || !isSignedIn) return null

  const backdrop = {
    position: 'fixed', inset: 0, zIndex: 600,
    background: 'rgba(7,39,45,0.48)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  }

  const card = {
    background: dark ? '#0b1c24' : '#f4f7f8',
    borderRadius: 12,
    border: `0.5px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'}`,
    boxShadow: '0 8px 40px rgba(0,0,0,0.20)',
    width: '100%', maxWidth: 740, maxHeight: '88vh',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
    position: 'relative',
  }

  const closeBtn = {
    position: 'absolute', top: 14, right: 16, zIndex: 10,
    background: 'none', border: 'none', cursor: 'pointer',
    color: dark ? 'rgba(207,226,232,0.35)' : 'rgba(7,39,45,0.30)',
    fontSize: 22, lineHeight: 1, padding: '2px 4px',
    fontFamily: 'sans-serif',
    transition: 'color 0.12s',
  }

  return (
    <div style={backdrop}>
      <div data-modal-id="AdminModal" style={card}>
        <button style={closeBtn} onClick={() => setOpen(false)} title="Close (Esc)">
          ×
        </button>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {seatReady && hasModule('admin')
            ? <AdminDashboard isModal />
            : (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 48,
                fontFamily: '"DM Sans", sans-serif', fontSize: 13,
                color: dark ? 'rgba(91,132,148,0.80)' : 'rgba(7,39,45,0.45)',
              }}>
                {seatReady ? 'Access restricted.' : 'Loading…'}
              </div>
            )
          }
        </div>
      </div>
    </div>
  )
}
